import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from './AuthContext';

const MaintenanceContext = createContext(null);

const POLL_MS = 60_000;  // re-fetch every 60s so admins see updates live

/** Provides the live maintenance config to the whole app.
 *  - Public GET, so it works for signed-out visitors too.
 *  - Polls every 60s + on tab refocus.
 *  - Exposes setConfig() for admin-side mutations (PUT-then-refresh).
 */
export function MaintenanceProvider({ children }) {
    const [cfg, setCfg] = useState(null);
    const [error, setError] = useState(false);
    const timerRef = useRef(null);

    const fetchCfg = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/maintenance`);
            setCfg(res.data);
            setError(false);
        } catch {
            // Silent — if the endpoint is unreachable, behave as "off" so
            // a broken /api/maintenance never bricks the app.
            setError(true);
        }
    }, []);

    useEffect(() => {
        fetchCfg();
        timerRef.current = setInterval(fetchCfg, POLL_MS);
        const onFocus = () => fetchCfg();
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(timerRef.current);
            window.removeEventListener('focus', onFocus);
        };
    }, [fetchCfg]);

    const setConfig = useCallback(async (next) => {
        const res = await axios.put(`${API}/maintenance`, next, { withCredentials: true });
        setCfg(res.data);
        return res.data;
    }, []);

    const value = {
        cfg: cfg || { mode: 'off', title: '', message: '', severity: 'info', expires_at: null, bypass_admins: true },
        loading: cfg === null && !error,
        refresh: fetchCfg,
        setConfig,
    };

    return (
        <MaintenanceContext.Provider value={value}>
            {children}
        </MaintenanceContext.Provider>
    );
}

export const useMaintenance = () => useContext(MaintenanceContext);
